import React from "react";
import { 
  Calendar, FileText, Users, BookOpen, GraduationCap, Award, 
  MapPin, Clock, Download, Phone, HelpCircle, ChevronRight,
  Trophy, Dumbbell, Timer, Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

// Academic Calendar Page
export const AcademicCalendarPage = () => {
  const calendarEvents = [
    { term: "First Term", start: "September 4, 2025", end: "December 20, 2025" },
    { term: "Second Term", start: "January 6, 2026", end: "April 10, 2026" },
    { term: "Third Term", start: "April 27, 2026", end: "July 31, 2026" },
  ];

  const holidays = [
    { name: "Independence Day", date: "March 6, 2026" },
    { name: "Easter Break", date: "April 3-6, 2026" },
    { name: "May Day", date: "May 1, 2026" },
    { name: "Founders' Day", date: "March 21, 2026" },
    { name: "Republic Day", date: "July 1, 2026" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-4">
            <Calendar size={18} />
            <span className="font-semibold text-sm">2025-2026</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Academic Calendar</h1>
        </div>

        <Card className="mb-6">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>School Terms</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {calendarEvents.map((event, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                  <span className="font-semibold text-[#002147]">{event.term}</span>
                  <span className="text-gray-600">{event.start} - {event.end}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-[#FF6B00] text-white rounded-t-lg">
            <CardTitle>Public Holidays</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              {holidays.map((holiday, index) => (
                <div key={index} className="flex justify-between items-center p-3 border rounded-lg">
                  <span className="font-medium">{holiday.name}</span>
                  <span className="text-gray-500 text-sm">{holiday.date}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Library Portal Page
export const LibraryPortalPage = () => {
  const navigate = useNavigate();
  
  const resources = [
    { title: "E-Books Collection", description: "Access thousands of digital books", icon: BookOpen },
    { title: "Research Databases", description: "Academic journals and papers", icon: FileText },
    { title: "Reading Lists", description: "Recommended books by grade level", icon: Calendar },
    { title: "Study Guides", description: "Exam preparation materials", icon: GraduationCap },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full mb-4">
            <BookOpen size={18} />
            <span className="font-semibold text-sm">Library</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Library Portal</h1>
          <p className="text-gray-700 mt-2">Access library resources and services</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {resources.map((resource, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <resource.icon className="text-orange-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#002147]">{resource.title}</h3>
                  <p className="text-gray-600 text-sm">{resource.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>Library Hours</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex justify-between"><span>Monday - Friday</span><span className="font-medium">7:30 AM - 5:00 PM</span></div>
              <div className="flex justify-between"><span>Saturday</span><span className="font-medium">9:00 AM - 1:00 PM</span></div>
              <div className="flex justify-between"><span>Sunday</span><span className="font-medium">Closed</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// JHS Exams Prep Page
export const CollegePrepPage = () => {
  const services = [
    { title: "BECE Preparation", description: "Comprehensive preparation for the Basic Education Certificate Examination" },
    { title: "Scholarship Information", description: "Available scholarships and how to apply" },
    { title: "Exam Preparation", description: "WASSCE, BECE, and university entrance exam prep" },
    { title: "Career Counseling", description: "Explore career paths and opportunities" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-600 px-4 py-2 rounded-full mb-4">
            <GraduationCap size={18} />
            <span className="font-semibold text-sm">Future Ready</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">JHS Exams Prep</h1>
          <p className="text-gray-700 mt-2">Preparing students for examination success</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {services.map((service, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <h3 className="font-semibold text-[#002147] text-lg mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>100% College Acceptance Rate</CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600">Our Class of 2025 achieved 100% acceptance to universities across Ghana and internationally.</p>
            <div className="mt-4 flex justify-center gap-8">
              <div><span className="text-3xl font-bold text-[#FF6B00]">50+</span><p className="text-sm text-gray-500">Graduates Yearly</p></div>
              <div><span className="text-3xl font-bold text-[#FF6B00]">100%</span><p className="text-sm text-gray-500">Acceptance Rate</p></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Athletics Page
export const AthleticsPage = () => {
  const sports = [
    { name: "Football", icon: Trophy, schedule: "Mon, Wed, Fri 3:30 PM" },
    { name: "Basketball", icon: Target, schedule: "Tue, Thu 3:30 PM" },
    { name: "Volleyball", icon: Dumbbell, schedule: "Mon, Wed 4:00 PM" },
    { name: "Track & Field", icon: Timer, schedule: "Daily 6:00 AM" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-600 px-4 py-2 rounded-full mb-4">
            <Award size={18} />
            <span className="font-semibold text-sm">Sports</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Athletics</h1>
          <p className="text-gray-700 mt-2">Building champions on and off the field</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {sports.map((sport, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <sport.icon className="text-yellow-600" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#002147]">{sport.name}</h3>
                  <p className="text-gray-600 text-sm">{sport.schedule}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>Athletic Achievements</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div><span className="text-2xl font-bold text-[#FF6B00]">100+</span><p className="text-sm text-gray-500">Trophies Won</p></div>
              <div><span className="text-2xl font-bold text-[#FF6B00]">15+</span><p className="text-sm text-gray-500">Regional Titles</p></div>
              <div><span className="text-2xl font-bold text-[#FF6B00]">50+</span><p className="text-sm text-gray-500">Athletes</p></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Campus Map Page
export const CampusMapPage = () => {
  const locations = [
    { name: "Main Administration Building", description: "Principal's office, Admissions, Records" },
    { name: "KG Block", description: "Kindergarten classrooms A & B" },
    { name: "Primary Block", description: "Primary 1-6 classrooms" },
    { name: "JHS Block", description: "JHS 1-3 classrooms" },
    { name: "Science Laboratory", description: "STEM activities and experiments" },
    { name: "Library", description: "Books, computers, study area" },
    { name: "Assembly Hall", description: "Morning assembly, events" },
    { name: "Sports Field", description: "Football, athletics, PE" },
    { name: "Canteen", description: "Lunch and refreshments" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-2 rounded-full mb-4">
            <MapPin size={18} />
            <span className="font-semibold text-sm">Campus</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Campus Map</h1>
          <p className="text-gray-700 mt-2">Find your way around our school</p>
        </div>

        <Card className="mb-6">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>Campus Locations</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              {locations.map((location, index) => (
                <div key={index} className="p-4 border rounded-lg hover:bg-gray-50">
                  <h3 className="font-semibold text-[#002147]">{location.name}</h3>
                  <p className="text-gray-600 text-sm">{location.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <MapPin className="mx-auto mb-4 text-[#FF6B00]" size={48} />
            <h3 className="font-semibold text-[#002147] mb-2">School Address</h3>
            <p className="text-gray-600">P.O. Box 682, Takoradi, Ghana</p>
            <p className="text-gray-600">Western Region</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Bell Schedule Page
export const BellSchedulePage = () => {
  const schedule = [
    { time: "6:30 AM", activity: "Gates Open" },
    { time: "7:00 AM", activity: "Morning Assembly" },
    { time: "7:30 AM", activity: "First Period" },
    { time: "8:30 AM", activity: "Second Period" },
    { time: "9:30 AM", activity: "Break Time" },
    { time: "10:00 AM", activity: "Third Period" },
    { time: "11:00 AM", activity: "Fourth Period" },
    { time: "12:00 PM", activity: "Lunch Break" },
    { time: "1:00 PM", activity: "Fifth Period" },
    { time: "2:00 PM", activity: "Sixth Period" },
    { time: "3:00 PM", activity: "Dismissal (KG & Lower Primary)" },
    { time: "3:30 PM", activity: "Dismissal (Upper Primary & JHS)" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-600 px-4 py-2 rounded-full mb-4">
            <Clock size={18} />
            <span className="font-semibold text-sm">Daily Schedule</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Bell Schedule</h1>
        </div>

        <Card>
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>Daily Timetable</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-3">
              {schedule.map((item, index) => (
                <div key={index} className={`flex justify-between items-center p-3 rounded-lg ${index % 2 === 0 ? 'bg-gray-50' : ''}`}>
                  <span className="font-semibold text-[#FF6B00]">{item.time}</span>
                  <span className="text-gray-700">{item.activity}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Forms & Documents Page
export const FormsDocumentsPage = () => {
  const navigate = useNavigate();
  
  const forms = [
    { name: "Enrollment Application", description: "New student registration form", path: "/resources/enrollment-application" },
    { name: "Medical Release Form", description: "Health and emergency information", path: "/resources/medical-release" },
    { name: "Field Trip Permission", description: "Parent consent for excursions", path: "/resources/field-trip-permission" },
    { name: "Transcript Request", description: "Request academic transcripts", path: "/resources/transcript-request" },
    { name: "Schedule Change Form", description: "Request class schedule changes", path: "/resources/schedule-change" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-600 px-4 py-2 rounded-full mb-4">
            <Download size={18} />
            <span className="font-semibold text-sm">Downloads</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Forms & Documents</h1>
          <p className="text-gray-700 mt-2">Download and submit required forms</p>
        </div>

        <Card>
          <CardHeader className="bg-[#002147] text-white rounded-t-lg">
            <CardTitle>Available Forms</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {forms.map((form, index) => (
                <div 
                  key={index} 
                  className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(form.path)}
                >
                  <div>
                    <h3 className="font-semibold text-[#002147]">{form.name}</h3>
                    <p className="text-gray-600 text-sm">{form.description}</p>
                  </div>
                  <ChevronRight className="text-gray-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// FAQ Page
export const FAQPage = () => {
  const faqs = [
    { question: "What are the school hours?", answer: "School begins at 7:00 AM with morning assembly. KG and Lower Primary dismiss at 3:00 PM, while Upper Primary and JHS dismiss at 3:30 PM." },
    { question: "How do I enroll my child?", answer: "Visit our Enrollment page or contact the Admissions Office. You'll need to complete the enrollment application and submit required documents." },
    { question: "What is the school fees payment schedule?", answer: "Fees are paid at the beginning of each term. We accept bank transfers, mobile money, and cash payments at the Accounts Office." },
    { question: "Does the school provide transportation?", answer: "Yes, we offer school bus services covering major areas in Takoradi. Contact the Transport Office for routes and fees." },
    { question: "What curriculum does the school follow?", answer: "We follow the Ghana Education Service (GES) curriculum with additional enrichment in STEM, Creative Arts, and Character Development." },
    { question: "How can I contact my child's teacher?", answer: "You can reach teachers through the Parent Portal messaging system or by scheduling a meeting through the school office." },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-lime-100 text-lime-600 px-4 py-2 rounded-full mb-4">
            <HelpCircle size={18} />
            <span className="font-semibold text-sm">Help</span>
          </div>
          <h1 className="text-3xl font-bold text-[#002147]">Frequently Asked Questions</h1>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b pb-4 last:border-b-0">
                  <h3 className="font-semibold text-[#002147] mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default {
  AcademicCalendarPage,
  LibraryPortalPage,
  CollegePrepPage,
  AthleticsPage,
  CampusMapPage,
  BellSchedulePage,
  FormsDocumentsPage,
  FAQPage
};
